// ============================================================================
// GST FILING PERIODS — G-1 slice 2c
// ============================================================================
// The filing obligations one GST engagement owes, with the live workflow state
// of each.
//
// The state column is the EWF instance's, joined server-side — GST stores no
// status of its own (GST-Module.md §4.2), so what this shows cannot disagree
// with the engine that owns it.
//
// The "Generate" action exists for two cases, and only those: engagements
// created BEFORE generation was wired into setup, and catching up after a
// scope or cadence change. It is idempotent, and the toast reports
// created-versus-already-present so a no-op run reads as a no-op rather than as
// silence.

import React, { useCallback, useEffect, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutGrid, type SyutGridColumn } from 'components/ui';
import { ConfirmDialog } from 'components/modals';
import notificationService from 'services/notificationService';
import { gstEngagementService } from 'api/services/gstEngagementService';
import { gstExpectedSourceService } from 'api/services/gstExpectedSourceService';
import type { GstEngagement, GstFilingPeriod, GstPeriodCompletenessSummary } from 'api/types/gstEngagement.types';
import GstPeriodSourcesModal from './GstPeriodSourcesModal';

interface Props {
  isOpen: boolean;
  engagement: GstEngagement | null;
  onClose: () => void;
  /** Whether the caller may trigger generation (the `edit` action). */
  canGenerate?: boolean;
}

const fyLabel = (y: number) => `FY ${y}-${String((y + 1) % 100).padStart(2, '0')}`;

const formatDate = (iso?: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

/**
 * Colour the workflow state so an overdue-looking period stands out from a
 * closed one. OPEN is the state every freshly generated period starts in, so it
 * is deliberately neutral rather than alarming.
 */
const stateBadgeClass = (code?: string | null): string => {
  switch (code) {
    case 'FILED':
    case 'PAID':
    case 'CLOSED':
      return 'badge badge-active';
    case 'APPROVED':
    case 'SENT_FOR_APPROVAL':
      return 'badge badge-info';
    default:
      return 'badge badge-inactive';
  }
};

const GstFilingPeriodsModal: React.FC<Props> = ({ isOpen, engagement, onClose, canGenerate = false }) => {
  const [periods, setPeriods] = useState<GstFilingPeriod[]>([]);
  const [summaries, setSummaries] = useState<Record<string, GstPeriodCompletenessSummary>>({});
  const [sourcesPeriod, setSourcesPeriod] = useState<GstFilingPeriod | null>(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Checked on open rather than discovered by failing. Generation refuses while
  // any definition is missing, and an error message is a poor place to learn
  // that a one-time tenant setup step was never run — especially when the only
  // way to run it was an API call the app never exposed.
  const [missingDefinitions, setMissingDefinitions] = useState<string[]>([]);
  const [installing, setInstalling] = useState(false);
  const [recomputing, setRecomputing] = useState(false);

  const loadSummaries = useCallback(async () => {
    if (!engagement) return;
    try {
      const list = await gstExpectedSourceService.getSummaries(engagement.id);
      setSummaries(Object.fromEntries(list.map((s) => [s.obligationId, s])));
    } catch {
      // The sources column is advisory on this screen; the guard enforces it
      // on the transition regardless, so a failed read just leaves dashes.
      setSummaries({});
    }
  }, [engagement]);

  const load = useCallback(async () => {
    if (!engagement) return;
    setLoading(true);
    try {
      setPeriods(await gstEngagementService.getFilingPeriods(engagement.id));
      void loadSummaries();
    } catch (err: any) {
      notificationService.toast.error(
        err?.response?.data?.message ?? 'Could not load return periods.',
      );
    } finally {
      setLoading(false);
    }
  }, [engagement, loadSummaries]);

  const checkDefinitions = useCallback(async () => {
    try {
      setMissingDefinitions(await gstEngagementService.getMissingWorkflowDefinitions());
    } catch {
      // Best-effort: if the check itself fails, generation will still refuse
      // with its own explicit message, so nothing is silently permitted.
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    void load();
    void checkDefinitions();
  }, [isOpen, load, checkDefinitions]);

  const handleInstallDefinitions = useCallback(async () => {
    setInstalling(true);
    try {
      const result = await gstEngagementService.installWorkflowDefinitions();
      notificationService.toast.success(
        `${result.installed.length} workflow definition(s) installed`
        + (result.alreadyInstalled.length > 0 ? `, ${result.alreadyInstalled.length} already present.` : '.'),
      );
      await checkDefinitions();
    } catch (err: any) {
      notificationService.toast.error(
        err?.response?.data?.message ?? 'Could not install GST workflow definitions.',
        8000,
      );
    } finally {
      setInstalling(false);
    }
  }, [checkDefinitions]);

  const handleGenerate = useCallback(async () => {
    if (!engagement) return;
    setConfirmOpen(false);
    setGenerating(true);
    try {
      const result = await gstEngagementService.generateFilingPeriods(engagement.id);

      // Report all three numbers. "0 created, 24 already present" is a correct
      // and reassuring outcome; showing only "0 created" would read as failure.
      const parts = [`${result.created} created`];
      if (result.alreadyPresent > 0) parts.push(`${result.alreadyPresent} already present`);
      if (result.skippedWithoutElection.length > 0) {
        parts.push(`${result.skippedWithoutElection.length} GSTIN(s) skipped — no filing frequency recorded`);
      }
      notificationService.toast.success(parts.join(', ') + '.');

      await load();
    } catch (err: any) {
      // The backend refuses generation when the six workflow definitions are
      // not installed, and its message names the endpoint that fixes it — so
      // surface the server's text rather than a generic failure.
      notificationService.toast.error(
        err?.response?.data?.message ?? 'Could not generate return periods.',
        8000,
      );
    } finally {
      setGenerating(false);
    }
  }, [engagement, load]);

  const handleRecomputeDueDates = useCallback(async () => {
    if (!engagement) return;
    setRecomputing(true);
    try {
      const result = await gstEngagementService.recomputeDueDates(engagement.id);
      const parts = [`${result.created} due date(s) stamped`];
      if (result.withoutDueDate) {
        parts.push(`${result.withoutDueDate} with no matching rule`);
      }
      notificationService.toast.success(parts.join(', ') + '.');
      await load();
    } catch (err: any) {
      notificationService.toast.error(
        err?.response?.data?.message ?? 'Could not recompute due dates.', 8000,
      );
    } finally {
      setRecomputing(false);
    }
  }, [engagement, load]);

  const missingDueDates = React.useMemo(
    () => periods.filter((p) => !p.dueDate).length,
    [periods],
  );

  const columns: SyutGridColumn<GstFilingPeriod>[] = React.useMemo(
    () => [
      {
        id: 'periodLabel',
        header: 'Period',
        accessorKey: 'periodLabel',
        size: 130,
        enableSorting: true,
        cell: ({ row }) => row.original.periodLabel || '—',
      },
      {
        id: 'returnType',
        header: 'Return',
        accessorKey: 'returnType',
        size: 110,
        enableSorting: true,
      },
      {
        id: 'gstin',
        header: 'GSTIN',
        accessorKey: 'gstin',
        size: 180,
        enableSorting: true,
        cell: ({ row }) => (
          <span style={{ fontFamily: 'monospace' }}>{row.original.gstin || '—'}</span>
        ),
      },
      {
        id: 'cadence',
        header: 'Period shape',
        accessorKey: 'cadence',
        size: 120,
        enableSorting: false,
      },
      {
        id: 'filingFrequency',
        header: 'Filing frequency',
        accessorKey: 'filingFrequency',
        size: 100,
        enableSorting: false,
        // Distinct from the shape on purpose: an IFF period is MONTHLY in shape
        // under the QRMP scheme (ADR-0019 / ADR-0020).
      },
      {
        id: 'dueDate',
        header: 'Due',
        accessorKey: 'dueDate',
        size: 170,
        enableSorting: true,
        // Shows BOTH dates when an extension applies. Displaying only the
        // effective one would hide that a deadline moved, which is exactly what
        // someone checking a late-fee exposure needs to see (ADR-0021 §1).
        cell: ({ row }) => {
          const p = row.original;
          if (!p.dueDate) {
            return <span style={{ color: 'var(--text-secondary)' }}>no rule</span>;
          }
          if (!p.extendedDueDate) return formatDate(p.dueDate);
          return (
            <span>
              <span style={{ textDecoration: 'line-through', color: 'var(--text-secondary)' }}>
                {formatDate(p.dueDate)}
              </span>
              {' → '}
              <strong>{formatDate(p.extendedDueDate)}</strong>
            </span>
          );
        },
      },
      {
        id: 'state',
        header: 'Status',
        accessorKey: 'workflowStateCode',
        size: 150,
        enableSorting: true,
        cell: ({ row }) => (
          <span className={stateBadgeClass(row.original.workflowStateCode)}>
            {row.original.workflowStateName || row.original.workflowStateCode || 'No workflow'}
          </span>
        ),
      },
      {
        // ADR-0025 §7: mandatory sources in / expected. Computed by the API on
        // every load, never stored, so a superseded file changes it at once.
        id: 'sources',
        header: 'Sources',
        size: 110,
        enableSorting: false,
        cell: ({ row }) => {
          const s = summaries[row.original.id];
          if (!s) return <span style={{ color: 'var(--text-secondary)' }}>—</span>;
          const open = () => setSourcesPeriod(row.original);
          if (!s.sourcesDeclared) {
            return <span style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }} title="No expected sources declared for this GSTIN">not set up</span>;
          }
          if (s.mandatoryExpected === 0) {
            return <button type="button" onClick={open} style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', color: 'var(--text-secondary)', fontSize: '0.75rem' }}>none needed</button>;
          }
          const done = s.mandatoryReady === s.mandatoryExpected;
          return (
            <button type="button" onClick={open} title="Show what is in and what is missing"
              className={done ? 'badge badge-active' : 'badge badge-pending'} style={{ border: 'none', cursor: 'pointer' }}>
              {s.mandatoryReady}/{s.mandatoryExpected} in
            </button>
          );
        },
      },
      {
        id: 'arn',
        header: 'Acknowledgement',
        accessorKey: 'arn',
        size: 160,
        enableSorting: false,
        cell: ({ row }) => row.original.arn || '—',
      },
    ],
    [summaries],
  );

  if (!isOpen || !engagement) return null;

  const title = engagement.clientLegalName || engagement.clientName || 'GST engagement';

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-container modal-lg">
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">
            Return periods — {title} · {fyLabel(engagement.financialYear)}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            <button className="modal-close" onClick={onClose} title="Close">
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content">
          {missingDefinitions.length > 0 && (
            <div
              style={{
                border: '1px solid #f59e0b', background: 'var(--bg-secondary)',
                borderRadius: 6, padding: '0.75rem', marginBottom: 12,
              }}
            >
              <div style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)', marginBottom: 4 }}>
                GST workflow definitions are not installed
              </div>
              <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginBottom: 8 }}>
                Filing periods carry their lifecycle on the workflow engine, so none can be created
                until this tenant has the {missingDefinitions.length} GST definition(s). This is a
                one-time setup per tenant.
              </div>
              {canGenerate && (
                <button
                  className="btn btn-primary"
                  onClick={handleInstallDefinitions}
                  disabled={installing}
                >
                  {installing ? 'Installing…' : 'Install definitions'}
                </button>
              )}
            </div>
          )}

          {!loading && periods.length > 0 && missingDueDates > 0 && (
            <div
              style={{
                border: '1px solid #f59e0b', background: 'var(--bg-secondary)',
                borderRadius: 6, padding: '0.625rem', marginBottom: 12,
                fontSize: '0.8125rem', color: 'var(--text-secondary)',
              }}
            >
              <strong style={{ color: 'var(--text-primary)' }}>
                {missingDueDates} period(s) have no due date.
              </strong>{' '}
              A period with no deadline never appears on a calendar sorted by deadline, so it would
              go unnoticed until it was overdue. Use <em>Recompute due dates</em> — or, if these
              return types have no rule recorded yet, add one first.
            </div>
          )}

          <div
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              marginBottom: 10, gap: 12,
            }}
          >
            <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
              {loading
                ? 'Loading…'
                : `${periods.length} obligation(s). Status comes from the workflow engine.`}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
            {canGenerate && periods.length > 0 && (
              <button
                className="btn btn-secondary"
                onClick={handleRecomputeDueDates}
                disabled={recomputing || loading}
                title="Recompute every period's statutory due date from the current rules"
              >
                {recomputing ? 'Recomputing…' : 'Recompute due dates'}
              </button>
            )}
            {canGenerate && (
              <button
                className="btn btn-secondary"
                onClick={() => setConfirmOpen(true)}
                disabled={generating || loading || missingDefinitions.length > 0}
                title={missingDefinitions.length > 0
                  ? 'Install the GST workflow definitions first'
                  : undefined}
              >
                {generating ? 'Generating…' : 'Generate missing periods'}
              </button>
            )}
            </div>
          </div>

          {!loading && periods.length === 0 && (
            <div
              style={{
                border: '1px dashed var(--border-color)', borderRadius: 6,
                padding: '1rem', fontSize: '0.8125rem', color: 'var(--text-secondary)',
              }}
            >
              No filing periods yet. Engagements configured before period generation was part of
              setup need one run of <strong>Generate missing periods</strong>; new ones get theirs
              automatically.
            </div>
          )}

          {periods.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <SyutGrid
                data={periods as any}
                columns={columns as any}
                enableSorting
                enablePagination
                pageSize={25}
                emptyMessage="No return periods."
              />
            </div>
          )}
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title="Generate return periods?"
        message={
          `This creates any obligation ${title} owes for ${fyLabel(engagement.financialYear)} `
          + 'that does not already exist, and starts a workflow for each. '
          + 'Existing periods are left untouched.'
        }
        confirmLabel="Generate"
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleGenerate}
        onCancel={() => setConfirmOpen(false)}
      />

      <GstPeriodSourcesModal
        isOpen={sourcesPeriod !== null}
        engagement={engagement}
        period={sourcesPeriod}
        canEdit={canGenerate}
        onClose={() => setSourcesPeriod(null)}
        onChanged={() => { void loadSummaries(); }}
      />
    </>
  );
};

export default GstFilingPeriodsModal;
